// src/component/YaeBook.js
// JSXなし / TypeScriptなし（純JS）。React.createElement 実装。
// 依存: react, react-pageflip, firebase v9, @react-pdf/renderer
// 前提: import { auth, db } from "../api/firebase"

import React, { useEffect, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import HTMLFlipBook from "react-pageflip";
import {
  collection,
  getDocs,
  getDoc,
  setDoc,
  doc,
  query,
  orderBy,
} from "firebase/firestore";
import { auth, db } from "../api/firebase";
import { useNavigate } from "react-router-dom";

// ★ PDF生成（react-pdf）
import {
  pdf,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";

/* ───────── ユーティリティ ───────── */
const el = React.createElement;
const MOBILE_BP = 768; // 一般スマホの目安
const FORCE_PORTRAIT_BP = 480; // 片面強制（iPhone SE対策）

const outer = () => ({
  width: "100%",
  display: "grid",
  placeItems: "center",
  padding: "16px",
});
const btnStyle = () => ({
  border: "1px solid #ddd",
  background: "#fff",
  padding: "4px 10px",
  borderRadius: 8,
  cursor: "pointer",
});
const toNum = (v, fb = null) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fb;
};
const toDateSafe = (ts, fb = null) => {
  try {
    if (!ts) return fb;
    if (typeof ts.toDate === "function") return ts.toDate();
    if (ts instanceof Date) return ts;
    return fb;
  } catch {
    return fb;
  }
};
const formatYMD = (d) => {
  if (!d) return "";
  const y = d.getFullYear(),
    m = String(d.getMonth() + 1).padStart(2, "0"),
    day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const formatDateTimeJP = (d) => {
  try {
    const dt = d ? new Date(d) : new Date();
    return dt.toLocaleString("ja-JP");
  } catch {
    return "";
  }
};

/* ───────── CSVユーティリティ ───────── */
const escapeCsv = (v) => {
  const s = v == null ? "" : String(v);
  return `"${s.replace(/"/g, '""')}"`; // 改行/カンマ/クォート対策
};
const toCsv = (rows) => rows.map((r) => r.map(escapeCsv).join(",")).join("\r\n");

/* ───────── 背景（色可変 和紙トーン）───────── */
function makePaperStyles(hexBase) {
  const base = hexBase || "#ffc0cb"; // デフォ: 桜ピンク
  function hex2rgba(h, a) {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h);
    if (!m) return `rgba(255,192,203,${a})`;
    const r = parseInt(m[1], 16),
      g = parseInt(m[2], 16),
      b = parseInt(m[3], 16);
    return `rgba(${r},${g},${b},${a})`;
  }
  const c1 = hex2rgba(base, 0.95);
  const c2 = hex2rgba(base, 0.75);
  const c3 = hex2rgba(base, 0.4);

  return {
    paperStyle: {
      width: "100%",
      height: "100%",
      background: `radial-gradient(120% 90% at 50% 10%, ${c1}, ${c2} 40%, ${c3})`,
      position: "relative",
      // ★ ページ内スクロールを許可
      overflowY: "auto",
      overflowX: "hidden",
      WebkitOverflowScrolling: "touch",
    },
    paperOverlay: {
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      mixBlendMode: "soft-light",
      backgroundImage:
        "repeating-linear-gradient(0deg, rgba(255,255,255,0.08), rgba(255,255,255,0.08) 2px, rgba(0,0,0,0) 2px, rgba(0,0,0,0) 14px)",
    },
    petalWatermark: {
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      background: `radial-gradient(40% 25% at 15% 20%, ${hex2rgba(
        base,
        0.18
      )}, transparent 70%), radial-gradient(35% 30% at 85% 80%, ${hex2rgba(
        base,
        0.15
      )}, transparent 70%)`,
    },
  };
}

/* ───────── ページ（固定コンポーネント／色はprops）───────── */
const BookPage = React.forwardRef(function BookPage(props, ref) {
  const { paperStyle, paperOverlay, petalWatermark } = makePaperStyles(
    props.colorHex
  );
  return el(
    "div",
    { ref, className: "page" },
    el(
      "div",
      { style: paperStyle },
      el("div", { style: paperOverlay }),
      el("div", { style: petalWatermark }),
      el(
        "div",
        {
          style: {
            position: "relative",
            padding: props.isMobile ? "18px" : "24px",
            height: "100%",
          },
        },
        props.children
      )
    )
  );
});

const CoverPage = React.forwardRef(function CoverPage(props, ref) {
  const { paperStyle, paperOverlay, petalWatermark } = makePaperStyles(
    props.colorHex
  );
  const title = props.title || "あなたの八重の誓い";
  return el(
    "div",
    { ref, className: "page" },
    el(
      "div",
      { style: paperStyle },
      el("div", { style: paperOverlay }),
      el("div", { style: petalWatermark }),
      el(
        "div",
        {
          style: {
            position: "relative",
            height: "100%",
            padding: props.isMobile ? "18px" : "24px",
            display: "grid",
            placeItems: "center",
            textAlign: "center",
          },
        },
        el(
          "div",
          {
            style: {
              opacity: 0.7,
              fontSize: props.isMobile ? 40 : 48,
              marginBottom: 8,
            },
          },
          "✨"
        ),
        el(
          "div",
          {
            style: {
              fontSize: props.isMobile ? 24 : 28,
              fontWeight: 800,
              letterSpacing: "1px",
            },
          },
          title
        ),
        el(
          "div",
          { style: { marginTop: 8, color: "#666" } },
          "一問一答の本をめくりながら、あなたの言葉をもう一度"
        )
      )
    )
  );
});

const ClosingPage = React.forwardRef(function ClosingPage(props, ref) {
  const { paperStyle, paperOverlay, petalWatermark } = makePaperStyles(
    props.colorHex
  );
  const footerNote = props.footerNote || "− 八重の誓い −";
  return el(
    "div",
    { ref, className: "page" },
    el(
      "div",
      { style: paperStyle },
      el("div", { style: paperOverlay }),
      el("div", { style: petalWatermark }),
      el(
        "div",
        {
          style: {
            position: "relative",
            height: "100%",
            padding: props.isMobile ? "18px" : "24px",
            display: "grid",
            placeItems: "center",
            textAlign: "center",
          },
        },
        el(
          "div",
          {
            style: {
              fontSize: props.isMobile ? 16 : 18,
              fontWeight: 700,
              marginBottom: 8,
            },
          },
          "八重の誓いプログラム"
        ),
        el("div", { style: { color: "#666" } }, footerNote)
      )
    )
  );
});

/* ==========================================================
   PDF（YaeBook: 回答済みのみ）生成
   ========================================================== */

// 日本語フォント（public/fonts/NotoSansJP-Regular.ttf を参照）
try {
  Font.register({
    family: "NotoSansJP",
    src: "/fonts/NotoSansJP-Regular.ttf",
  });
} catch (_) {}

const pdfStyles = StyleSheet.create({
  page: { padding: 28, fontFamily: "NotoSansJP", fontSize: 11, lineHeight: 1.6 },
  title: { fontSize: 18, fontWeight: 800, marginBottom: 6 },
  sub: { fontSize: 10, color: "#666", marginBottom: 14 },

  section: {
    marginBottom: 10,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
  },
  row: { flexDirection: "row", justifyContent: "space-between", marginBottom: 6 },
  qNo: { fontSize: 11, fontWeight: 800 },
  date: { fontSize: 10, color: "#666" },

  question: { fontSize: 12, fontWeight: 800, marginBottom: 6 },
  label: { fontSize: 10, color: "#666", marginBottom: 2 },
  panel: { padding: 8, borderRadius: 6, backgroundColor: "#f7f7f7", marginBottom: 6 },
  panelText: { fontSize: 10, color: "#333" },

  answer: { fontSize: 11, color: "#111" },
  empty: { fontSize: 11, color: "#999" },
});

// JSXなしで Document を組む
function YaeBookPdfDoc(params) {
  const title = params.title || "あなたの八重の誓い";
  const createdAt = params.createdAt || new Date();
  const answeredCount = params.answeredCount || 0;
  const qaPages = params.qaPages || [];

  return el(
    Document,
    null,
    el(
      Page,
      { size: "A4", style: pdfStyles.page },
      el(Text, { style: pdfStyles.title }, title),
      el(
        Text,
        { style: pdfStyles.sub },
        "作成日時: ",
        formatDateTimeJP(createdAt),
        " / 回答数: ",
        String(answeredCount)
      ),

      ...qaPages.map((p) => {
        const dateStr = p.answeredAt ? formatYMD(p.answeredAt) : "";
        return el(
          View,
          { key: p.id || String(p.no || Math.random()), style: pdfStyles.section },
          el(
            View,
            { style: pdfStyles.row },
            el(Text, { style: pdfStyles.qNo }, p.no != null ? `Q${p.no}` : "Q"),
            el(Text, { style: pdfStyles.date }, dateStr ? `回答日: ${dateStr}` : "")
          ),

          el(Text, { style: pdfStyles.question }, p.question || ""),

          p.challenge
            ? el(
                View,
                { style: pdfStyles.panel },
                el(Text, { style: pdfStyles.label }, "挑戦"),
                el(Text, { style: pdfStyles.panelText }, String(p.challenge))
              )
            : null,

          p.gain
            ? el(
                View,
                { style: pdfStyles.panel },
                el(Text, { style: pdfStyles.label }, "何を得るか"),
                el(Text, { style: pdfStyles.panelText }, String(p.gain))
              )
            : null,

          el(Text, { style: pdfStyles.label }, "あなたの回答"),
          el(
            Text,
            { style: p.answer ? pdfStyles.answer : pdfStyles.empty },
            p.answer ? String(p.answer) : "（空）"
          )
        );
      })
    )
  );
}

/* ───────── メイン：YaeBook ───────── */
function YaeBook(props) {
  const [uid, setUid] = useState(
    props.userId || (auth.currentUser && auth.currentUser.uid) || null
  );
  const [pages, setPages] = useState([]); // cover + answered(QA)* + closing
  const [loading, setLoading] = useState(true);

  // 背景色（ユーザ設定）
  const [colorHex, setColorHex] = useState("#ffc0cb"); // 既定：桜ピンク
  const [savingColor, setSavingColor] = useState(false);

  // ★ PDF生成中
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const navigate = useNavigate();

  // 画面幅/高さでレスポンシブ
  const [viewportW, setViewportW] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  const [viewportH, setViewportH] = useState(
    typeof window !== "undefined" ? window.innerHeight : 768
  );

  const isMobile = viewportW < MOBILE_BP;
  const forcePortrait = viewportW <= FORCE_PORTRAIT_BP; // iPhone SE などは常に片面

  const bookRef = useRef(null);

  // ユーザー設定（色）ロード + ログインガード
  useEffect(
    function () {
      (async function guardAndLoad() {
        // 未ログインなら即ホームへ
        if (!uid) {
          navigate("/home", { replace: true });
          return;
        }

        try {
          const userRef = doc(db, "users", uid);
          const userSnap = await getDoc(userRef);
          const data = userSnap.exists() ? userSnap.data() || {} : {};

          if (typeof data.yaeBookColor === "string" && data.yaeBookColor.trim()) {
            setColorHex(data.yaeBookColor);
          }
        } catch (_) {
          // 取得失敗時も安全側でホームへ
          navigate("/home", { replace: true });
        }
      })();
    },
    [uid, navigate]
  );

  // リサイズ監視
  useEffect(function () {
    function onResize() {
      setViewportW(window.innerWidth);
      setViewportH(window.innerHeight);
    }
    onResize();
    window.addEventListener("resize", onResize);
    return function () {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  // uid の安定取得
  useEffect(
    function () {
      if (props.userId) {
        setUid(props.userId);
        return;
      }
      const unsub = onAuthStateChanged(auth, function (u) {
        setUid(u ? u.uid : null);
      });
      return function () {
        unsub && unsub();
      };
    },
    [props.userId]
  );

  // ユーザー設定（色）ロード（フォールバック）
  useEffect(
    function () {
      (async function loadColor() {
        try {
          if (!uid) return;
          const userDoc = await getDoc(doc(db, "users", uid));
          const data = userDoc.exists() ? userDoc.data() || {} : {};
          if (typeof data.yaeBookColor === "string" && data.yaeBookColor.trim()) {
            setColorHex(data.yaeBookColor);
          }
        } catch (_) {}
      })();
    },
    [uid]
  );

  // 質問・回答のロード
  useEffect(
    function () {
      (async function run() {
        try {
          if (!uid) {
            setPages([]);
            setLoading(false);
            return;
          }
          setLoading(true);

          /* 1) 回答（回答済みのみ採用）
             回答テキストの互換: text / answer / value / content / body */
          const ansSnap = await getDocs(collection(db, "users", uid, "yaeChikaiAnswers"));
          const answers = [];
          ansSnap.forEach((d) => {
            const x = d.data() || {};
            const textRaw = x.text ?? x.answer ?? x.value ?? x.content ?? x.body ?? "";
            const text = String(textRaw || "").trim();
            if (!text) return; // 未回答は除外

            const answeredAt =
              toDateSafe(x.answeredAt, null) ||
              toDateSafe(x.createdAt, null) ||
              (d.createTime && typeof d.createTime.toDate === "function"
                ? d.createTime.toDate()
                : null);

            answers.push({
              id: d.id,
              questionId: x.questionId || null,
              no: toNum(x.no, null), // 回答側に no があれば使う
              text,
              answeredAt,
            });
          });

          if (!answers.length) {
            setPages([{ type: "cover" }, { type: "closing" }]);
            return;
          }

          /* 2) 質問（YaeNoInori → yaeNoInori フォールバック） */
          async function fetchQuestions(collName) {
            const c = collection(db, collName);
            try {
              return await getDocs(query(c, orderBy("no", "asc")));
            } catch {
              try {
                return await getDocs(query(c, orderBy("No", "asc")));
              } catch {
                return await getDocs(c);
              }
            }
          }
          let qSnap = await fetchQuestions("YaeNoInori");
          if (!qSnap || qSnap.empty) qSnap = await fetchQuestions("yaeNoInori");

          const qById = new Map();
          const qByNo = new Map();
          qSnap.forEach((d) => {
            const qd = d.data() || {};
            const no = toNum(qd.no != null ? qd.no : qd.No, null);
            const stepContent = qd.stepContent || qd.text || ""; // 質問文
            const challenge = qd.challenge || ""; // 挑戦
            const gain = qd.gain || ""; // 何を得るか
            const entry = { id: d.id, no, stepContent, challenge, gain };
            qById.set(d.id, entry);
            if (no != null) qByNo.set(no, entry);
          });

          /* 3) 回答×質問を ID → no の順でマージ（回答済みのみ） */
          const qaPages = answers.map((a, i) => {
            const q =
              (a.questionId && qById.get(a.questionId)) ||
              qById.get(a.id) ||
              (a.no != null ? qByNo.get(a.no) : null);

            const no =
              (q && q.no != null ? q.no : null) ??
              (a.no != null ? a.no : null) ??
              i + 1; // 最後の手段で連番

            return {
              type: "qa",
              id: (q && q.id) || a.id,
              no,
              question: (q && q.stepContent) || `Q${no}`,
              challenge: (q && q.challenge) || "",
              gain: (q && q.gain) || "",
              answer: a.text,
              answeredAt: a.answeredAt,
            };
          });

          // no 昇順
          qaPages.sort((p, q) => {
            const pn = p.no != null ? p.no : Number.MAX_SAFE_INTEGER;
            const qn = q.no != null ? q.no : Number.MAX_SAFE_INTEGER;
            return pn - qn;
          });

          setPages([{ type: "cover" }].concat(qaPages).concat([{ type: "closing" }]));
        } finally {
          setLoading(false);
        }
      })();
    },
    [uid]
  );

  /* 背景色の保存 */
  async function saveColor(hex) {
    if (!uid || !hex) return;
    try {
      setSavingColor(true);
      await setDoc(doc(db, "users", uid), { yaeBookColor: hex }, { merge: true });
    } finally {
      setSavingColor(false);
    }
  }

  /* ページ操作 */
  const turnPrev = () => {
    if (bookRef.current) bookRef.current.pageFlip().flipPrev();
  };
  const turnNext = () => {
    if (bookRef.current) bookRef.current.pageFlip().flipNext();
  };

  /* ★ PDFダウンロード */
  async function downloadPdf() {
    try {
      if (downloadingPdf) return;
      setDownloadingPdf(true);

      const qaPages = pages.filter((p) => p && p.type === "qa");
      const answeredCount = qaPages.length;

      const title = "あなたの八重の誓い";
      const createdAt = new Date();

      const blob = await pdf(
        YaeBookPdfDoc({
          title,
          createdAt,
          answeredCount,
          qaPages,
        })
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `yae-book_${formatYMD(new Date()) || "download"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingPdf(false);
    }
  }

  /* ★ CSVダウンロード */
  async function downloadCsv() {
    const qaPages = pages.filter((p) => p && p.type === "qa");

    const header = ["no", "question", "challenge", "gain", "answer", "answeredAt"];
    const rows = qaPages.map((p) => [
      p.no != null ? p.no : "",
      p.question || "",
      p.challenge || "",
      p.gain || "",
      p.answer || "",
      p.answeredAt ? formatYMD(p.answeredAt) : "",
    ]);

    const csv = toCsv([header].concat(rows));

    // Excel対策：UTF-8 BOM
    const bom = "\uFEFF";
    const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `yae-book_${formatYMD(new Date()) || "download"}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  /* 色選択UI（プリセット + カラーピッカー） */
  const presets = [
    "#ffc0cb",
    "#ffd1a4",
    "#d1ffd6",
    "#cde7ff",
    "#e0ccff",
    "#ffe4b5",
    "#f4cccc",
  ];
  const colorBar = el(
    "div",
    {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 10,
        flexWrap: "wrap",
        justifyContent: "center",
      },
    },
    el("div", { style: { fontSize: 12, color: "#666", marginRight: 6 } }, "背景色："),
    ...presets.map((c) =>
      el("button", {
        key: c,
        onClick: async () => {
          setColorHex(c);
          await saveColor(c);
        },
        title: c,
        style: {
          width: 22,
          height: 22,
          borderRadius: 999,
          border: c === colorHex ? "2px solid #333" : "1px solid #bbb",
          background: c,
          cursor: "pointer",
        },
      })
    ),
    el("input", {
      type: "color",
      value: colorHex,
      onChange: async (e) => {
        const v = e.target.value;
        setColorHex(v);
        await saveColor(v);
      },
      style: {
        width: 36,
        height: 24,
        border: "none",
        background: "transparent",
        cursor: "pointer",
      },
    }),
    el("span", { style: { fontSize: 12, color: "#666" } }, savingColor ? "保存中..." : "")
  );

  /* レイアウト（A判っぽい比率 & 可変サイズ） */
  const aspect = 1.414; // A判 √2
  const availW = Math.max(320, viewportW - 32);
  const basePageW = forcePortrait ? Math.min(availW, 480) : 520;
  const wantedH = Math.round(basePageW * (forcePortrait ? 1.55 : aspect));
  const maxH = Math.max(360, viewportH - 160); // ヘッダ・色バー分を引いた安全値
  const pageWidth = basePageW;
  const pageHeight = Math.min(wantedH, maxH);

  // ヘッダー（回答件数表示 + DLボタン）
  const answeredCount = Math.max(0, pages.length - 2); // cover/closing を除く
  const header = el(
    "div",
    {
      style: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "8px",
        justifyContent: "center",
        color: "#666",
        fontSize: 12,
        flexWrap: "wrap",
      },
    },
    el("button", { onClick: turnPrev, style: btnStyle() }, "←"),
    el(
      "div",
      { style: { maxWidth: 520, textAlign: "center" } },
      `回答済み ${answeredCount} ページ / 本をクリック／ドラッグでもめくれます`
    ),
    el("button", { onClick: turnNext, style: btnStyle() }, "→"),

    // PDF
    el(
      "button",
      {
        onClick: downloadPdf,
        disabled: downloadingPdf || answeredCount === 0,
        style: Object.assign({}, btnStyle(), {
          border: "1px solid rgba(0,0,0,0.18)",
          background: downloadingPdf ? "rgba(0,0,0,0.04)" : "#fff",
          opacity: downloadingPdf ? 0.75 : 1,
        }),
        title: "回答済みページをPDFで保存します",
      },
      downloadingPdf ? "PDF作成中..." : "PDFダウンロード"
    ),

    // CSV
    el(
      "button",
      {
        onClick: downloadCsv,
        disabled: answeredCount === 0,
        style: Object.assign({}, btnStyle(), {
          border: "1px solid rgba(0,0,0,0.18)",
          background: "#fff",
        }),
        title: "回答済みページをCSVで保存します",
      },
      "CSVダウンロード"
    )
  );

  if (loading) {
    return el(
      "div",
      { style: outer() },
      colorBar,
      header,
      el(
        "div",
        { style: { height: 400, display: "grid", placeItems: "center" } },
        "読み込み中..."
      )
    );
  }

  if (answeredCount === 0) {
    return el(
      "div",
      { style: outer() },
      colorBar,
      header,
      el("div", { style: { color: "#666" } }, "まだ表示できる回答がありません。")
    );
  }

  return el(
    "div",
    { style: outer() },
    colorBar,
    header,
    el(
      HTMLFlipBook,
      {
        ref: bookRef,
        width: pageWidth,
        height: pageHeight,
        size: "stretch",
        minWidth: 300,
        maxWidth: 480,
        minHeight: 320,
        maxHeight: Math.max(360, maxH),
        maxShadowOpacity: forcePortrait ? 0.15 : 0.3,
        showCover: !forcePortrait, // 幅<=480 は常に片面で
        usePortrait: forcePortrait, // ★ 片面強制（iPhone SE対策）
        mobileScrollSupport: true,
        drawShadow: !forcePortrait,
        autoSize: true,
        style: {
          boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
          borderRadius: 12,
          margin: "0 auto",
          touchAction: "pan-y",
        },
        className: "yae-book",
      },
      ...pages.map((p, idx) => {
        if (p.type === "cover")
          return el(CoverPage, {
            key: "cover",
            colorHex,
            isMobile: isMobile,
          });

        if (p.type === "closing")
          return el(ClosingPage, {
            key: "closing",
            colorHex,
            isMobile: isMobile,
          });

        const dateStr = formatYMD(p.answeredAt);

        return el(
          BookPage,
          { key: p.id || idx, colorHex, isMobile: isMobile },
          // Q + no
          el(
            "div",
            {
              style: {
                fontSize: forcePortrait ? 14 : 16,
                fontWeight: 700,
                marginBottom: 6,
              },
            },
            p.no != null ? `Q${p.no}` : "Q"
          ),

          // 質問
          el(
            "div",
            {
              style: {
                fontSize: forcePortrait ? 20 : 22,
                fontWeight: 800,
                lineHeight: 1.35,
                marginBottom: 10,
              },
            },
            p.question
          ),

          // 情報パネル（挑戦 / 何を得るか）
          (function () {
            const panelBase = {
              borderRadius: 10,
              padding: forcePortrait ? "10px 12px" : "12px 14px",
              margin: "8px 0",
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
            };
            const labelBase = {
              display: "inline-block",
              fontWeight: 800,
              fontSize: forcePortrait ? 12 : 13,
              letterSpacing: "0.2px",
              marginBottom: 6,
              padding: "2px 8px",
              borderRadius: 999,
            };

            const challengePanel = p.challenge
              ? el(
                  "div",
                  {
                    style: Object.assign({}, panelBase, {
                      background: "rgba(33, 150, 243, 0.06)",
                      borderLeft: "4px solid rgba(33,150,243,0.6)",
                    }),
                  },
                  el(
                    "span",
                    {
                      style: Object.assign({}, labelBase, {
                        background: "rgba(33,150,243,0.12)",
                        color: "rgb(33, 70, 120)",
                      }),
                    },
                    "挑戦"
                  ),
                  el(
                    "div",
                    { style: { fontSize: forcePortrait ? 14 : 15, color: "#333" } },
                    p.challenge
                  )
                )
              : null;

            const gainPanel = p.gain
              ? el(
                  "div",
                  {
                    style: Object.assign({}, panelBase, {
                      background: "rgba(233, 30, 99, 0.06)",
                      borderLeft: "4px solid rgba(233,30,99,0.6)",
                    }),
                  },
                  el(
                    "span",
                    {
                      style: Object.assign({}, labelBase, {
                        background: "rgba(233,30,99,0.12)",
                        color: "rgb(120, 24, 60)",
                      }),
                    },
                    "何を得るか"
                  ),
                  el(
                    "div",
                    {
                      style: {
                        fontSize: forcePortrait ? 14 : 15,
                        color: "#33111d",
                        fontWeight: 600,
                      },
                    },
                    p.gain
                  )
                )
              : null;

            return el(React.Fragment, null, challengePanel, gainPanel);
          })(),

          // 回答本文
          el(
            "div",
            {
              style: {
                marginTop: 10,
                whiteSpace: "pre-wrap",
                lineHeight: 1.9,
                letterSpacing: "0.2px",
                fontSize: forcePortrait ? 15 : 16,
                wordBreak: "break-word",
              },
            },
            p.answer
          ),

          // 解答日
          el(
            "div",
            {
              style: {
                textAlign: "right",
                color: "#777",
                fontSize: forcePortrait ? 11 : 12,
                marginTop: 12,
              },
            },
            dateStr ? `回答日: ${dateStr}` : ""
          ),

          // ビューワ通し番号
          el(
            "div",
            {
              style: {
                position: "absolute",
                bottom: 10,
                left: 0,
                right: 0,
                textAlign: "center",
                color: "#999",
                fontSize: forcePortrait ? 11 : 12,
              },
            },
            idx,
            " / ",
            pages.length - 1
          )
        );
      })
    )
  );
}

export default YaeBook;
